import React, { useState } from 'react';
import { X, AlertTriangle, AlertCircle, AlertOctagon, Info, RefreshCw } from 'lucide-react';
import { useErrorContext } from '../../contexts/ErrorContext';

interface ErrorNotificationProps {
  className?: string;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
  maxNotifications?: number;
  autoDismiss?: boolean;
  autoDismissDelay?: number;
}

const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  className = '',
  position = 'top-right',
  maxNotifications = 5,
  autoDismiss = true,
  autoDismissDelay = 5000
}) => {
  const { errors, dismissError, dismissAllErrors } = useErrorContext();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2'
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon className="w-5 h-5" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5" />;
      case 'low':
        return <Info className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500 border-red-500 text-white';
      case 'high':
        return 'bg-orange-500 border-orange-500 text-white';
      case 'medium':
        return 'bg-yellow-500 border-yellow-500 text-white';
      case 'low':
        return 'bg-blue-500 border-blue-500 text-white';
      default:
        return 'bg-gray-500 border-gray-500 text-white';
    }
  };

  const handleDismiss = (id: string) => {
    dismissError(id);
    setDismissedIds(prev => new Set(Array.from(prev).concat(id)));
  };

  const handleDismissAll = () => {
    dismissAllErrors();
    setDismissedIds(new Set());
  };

  // Filter out dismissed notifications and limit count
  const visibleErrors = errors
    .filter(error => !error.dismissed && !dismissedIds.has(error.id))
    .slice(0, maxNotifications);

  // Auto-dismiss notifications
  React.useEffect(() => {
    if (!autoDismiss) return;

    const timers = visibleErrors.map(error => {
      return setTimeout(() => {
        handleDismiss(error.id);
      }, autoDismissDelay);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [visibleErrors, autoDismiss, autoDismissDelay]);

  if (visibleErrors.length === 0) {
    return null;
  }

  return (
    <div className={`fixed z-50 space-y-2 ${positionClasses[position]} ${className}`} data-testid="error-notifications">
      {visibleErrors.map((error) => (
        <div
          key={error.id}
          className={`max-w-sm w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border-l-4 ${getSeverityColor(error.severity)} transform transition-all duration-300 ease-in-out`}
          style={{
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {getSeverityIcon(error.severity)}
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">
                    {error.severity.charAt(0).toUpperCase() + error.severity.slice(1)} Error
                  </h3>
                  <button
                    onClick={() => handleDismiss(error.id)}
                    className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {error.message}
                </p>
                {error.code && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Code: {error.code}
                  </p>
                )}
                {error.source && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Source: {error.source}
                  </p>
                )}
                {error.retryable && (
                  <div className="mt-2">
                    <button
                      onClick={() => {
                        // In a real app, this would trigger a retry
                        handleDismiss(error.id);
                      }}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {visibleErrors.length > 1 && (
        <div className="flex justify-end">
          <button
            onClick={handleDismissAll}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Dismiss all
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ErrorNotification;
